import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { isPwaMode } from '@/utils/pwaUtils';
import {
  getAuthToken,
  validateAuthToken,
  clearAuthToken,
  shouldUsePwaTokenAuth
} from '@/utils/auth/tokenAuthUtils';
import { useToast } from './use-toast';
import { logger } from "@/utils/logger";

// Global reference for the interval to allow control from outside hooks
let tokenCheckInterval: number | null = null;

/**
 * Starts the periodic token validity checker
 */
export const startTokenValidityChecker = () => {
  if (tokenCheckInterval) return; // Already running

  logger.info('Starting periodic token validity checker');

  // Check every 5 minutes (300000 ms)
  tokenCheckInterval = window.setInterval(async () => {
    if (!isPwaMode()) {
      stopTokenValidityChecker();
      return;
    }

    const token = await getAuthToken();
    if (!token) {
      stopTokenValidityChecker();
      return;
    }

    // Validate token silently
    try {
      const { valid, revoked } = await validateAuthToken(token);
      if (!valid || revoked) {
        logger.info('Periodic check: Token invalid or revoked');
        await clearAuthToken();
        stopTokenValidityChecker();
        // Force reload to trigger auth guards
        window.location.reload();
      }
    } catch (error) {
      logger.error('Periodic check error:', error);
    }
  }, 5 * 60 * 1000);
};

/**
 * Stops the periodic token validity checker
 */
export const stopTokenValidityChecker = () => {
  if (tokenCheckInterval) {
    logger.info('Stopping periodic token validity checker');
    window.clearInterval(tokenCheckInterval);
    tokenCheckInterval = null;
  }
};

/**
 * Hook to handle token authentication in PWA mode
 */
export function useTokenAuth() {
  const [hasToken, setHasToken] = useState(false);
  const [isTokenValid, setIsTokenValid] = useState<boolean | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const isPwa = isPwaMode();

  // Check if there's a token and validate it
  const checkAndValidateToken = useCallback(async () => {
    if (!isPwa) return false;

    const hasAuth = await shouldUsePwaTokenAuth();
    setHasToken(hasAuth);

    if (!hasAuth) return false;

    const token = await getAuthToken();
    if (!token) return false;

    try {
      setIsValidating(true);
      const { valid, revoked } = await validateAuthToken(token);

      // Handle revoked token immediately
      if (revoked) {
        logger.info('useTokenAuth: Token has been revoked');
        await clearAuthToken();
        setIsTokenValid(false);
        handleInvalidToken();
        return false;
      }

      setIsTokenValid(valid);
      return valid;
    } catch (error) {
      logger.error('Error validating token:', error);
      setIsTokenValid(false);
      return false;
    } finally {
      setIsValidating(false);
    }
  }, [isPwa]);

  // Handle invalid token
  const handleInvalidToken = useCallback(async () => {
    await clearAuthToken();
    setIsTokenValid(false);
    setHasToken(false);
    stopTokenValidityChecker();

    toast({
      title: 'Authentication expired',
      description: 'Your session has expired. Please sign in again.',
      variant: 'destructive',
    });

    window.location.href = '/';
  }, [toast]);

  // Initialize token validation on mount
  useEffect(() => {
    const initializeAuth = async () => {
      if (!isPwa) return;

      const hasAuth = await shouldUsePwaTokenAuth();
      if (!hasAuth) return;

      // Check immediate token validity
      const isValid = await checkAndValidateToken();

      // If valid, start periodic checker
      if (isValid) {
        startTokenValidityChecker();
      }
    };

    initializeAuth();

    // Cleanup on unmount
    return () => {
      stopTokenValidityChecker();
    };
  }, [isPwa, checkAndValidateToken]);

  return {
    isPwa,
    hasToken,
    isTokenValid,
    isValidating,
    checkAndValidateToken,
    handleInvalidToken,
  };
}
